import { getGiveawayStockStatus, getIsCouponCriterionApplied } from '@cart-services/core/couponService'
import { CartItemImage } from '@cart-ui/CartListItem/CartItemImage'
import { CartListItemLayout } from '@cart-ui/CartListItem/CartListItemLayout'
import { IconButtonDeleteItem } from '@cart-ui/CartListItem/IconButtonDeleteItem'
import { SelectProductVariant } from '@cart-ui/CartListItem/SelectProductVariant'
import { SoldOutText } from '@cart-ui/CartListItem/SoldOutText'
import { CartSalePrice } from '@cart-ui/CartSalePrice/CartSalePrice'
import { CouponCondition } from '@cart-ui/CouponCondition/CouponCondition'
import { type CouponConditionListProps } from '@cart-ui/CouponConditionList/CouponConditionList'
import { STOCK_WARNING_LIMIT } from '@constants'
import { Chip } from '@pets-ui/Chip/Chip'
import { Fade } from '@pets-ui/Fade/Fade'
import { HStack } from '@pets-ui/HStack/HStack'
import { NumberInput } from '@pets-ui/NumberInput/NumberInput'
import { Skeleton } from '@pets-ui/Skeleton/Skeleton'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import { type MouseEventHandler, type ReactNode } from 'react'

export interface CartListItemProps {
  quantity?: number
  maxQuantity?: number
  salePrice?: number
  attributes?: string
  originalPrice?: number
  stock?: number
  productImgSrc?: string
  productTitle?: ReactNode
  currency?: string
  isAddon?: boolean
  couponCriterions?: CouponConditionListProps['couponCriterions']
  isLoading?: boolean
  onQuantityChange?: ((value?: number) => void)
  onDelete?: MouseEventHandler<HTMLButtonElement>
  onSelectVariantClick?: () => void
}

export const CartListItem = (props: CartListItemProps) => {
  const {
    quantity,
    maxQuantity,
    salePrice,
    originalPrice,
    stock = 0,
    productImgSrc,
    productTitle,
    currency,
    couponCriterions,
    onQuantityChange,
    onDelete,
    onSelectVariantClick,
    attributes,
    isAddon,
    isLoading,
  } = props
  /** 避免 stock 為負值 */
  const normalizedStock = Math.max(stock, 0)
  const isOutOfStock = normalizedStock <= 0
  const availableQuantity = normalizedStock - (quantity ?? 0)
  const isMaxQuantity = maxQuantity === quantity
  // TODO: i18n
  const quantityDisplay = () => {
    if (availableQuantity <= 0 || isMaxQuantity) {
      return '已達庫存上限'
    }
    if (availableQuantity <= STOCK_WARNING_LIMIT) {
      return `剩餘數量 ${availableQuantity}`
    }
  }
  const chipText = isAddon && '加價購'

  const handleQuantityChange: CartListItemProps['onQuantityChange'] = (value) => {
    if (value === undefined) return
    onQuantityChange?.(value)
  }

  return (
    <VStack p={3} gap={3} backgroundColor={(isOutOfStock && !isLoading) ? colors.red100 : 'transparent'}>
      <CartListItemLayout>
        <CartItemImage
          productImgSrc={productImgSrc}
          productTitle={productTitle}
        />
        <VStack gap={1}>
          <HStack gap={2} alignItems="center" flexWrap="nowrap">
            {chipText && <Chip size="small" variant="square" color="primary">{chipText}</Chip>}
            <Text variant="subtitle">
              {productTitle}
            </Text>
          </HStack>
          <SelectProductVariant onClick={onSelectVariantClick}>
            {attributes}
          </SelectProductVariant>
        </VStack>
        <HStack gap={2} alignItems="center">
          <CartSalePrice currency={currency} salePrice={salePrice} originalPrice={originalPrice} />
          {isLoading
            ? <Skeleton variant="text" width={92} height={25} />
            : normalizedStock > 0
              ? (
                <VStack gap={1} alignItems="center">
                  <NumberInput
                    value={quantity}
                    min={normalizedStock > 0 ? 1 : 0}
                    max={maxQuantity ?? normalizedStock}
                    onChange={handleQuantityChange}
                    autoSelect
                  />
                  {(availableQuantity <= STOCK_WARNING_LIMIT || isMaxQuantity) && <Text variant="caption" color={colors.green400}>{quantityDisplay()}</Text> }
                </VStack>
              )
              : <SoldOutText>已售完</SoldOutText>}
        </HStack>
        <IconButtonDeleteItem onClick={onDelete} />
      </CartListItemLayout>
      <Fade in={couponCriterions && couponCriterions.length > 0} duration={300}>
        <VStack gap={1}>
          {couponCriterions?.map(criterion => (
            <CouponCondition
              key={criterion.id}
              displayName={criterion.display_name}
              isApplied={getIsCouponCriterionApplied(criterion)}
              currency={currency}
              moreAmount={criterion.left_amount}
              moreQuantity={criterion.left_quantity ?? criterion.display_criterion_quantity}
              count={criterion.count}
              giveawayStockStatus={getGiveawayStockStatus(criterion.giveaways)}
            />
          ))}
        </VStack>
      </Fade>
    </VStack>
  )
}
